import { useMemo, useCallback } from 'react';
import { useSuggestions } from '../../hooks/useSuggestions';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import SuggestionItem from './suggestionItem';

// Constantes
const MOBILE_SUGGESTIONS_LIMIT = 3;
const DESKTOP_SUGGESTIONS_LIMIT = 6;
const TEXT_CLEANUP_REGEX = {
  REMOVE_LAST_WORD: /\s+\S*$/,
  REMOVE_LEADING_SPECIAL: /^[^a-zA-Z0-9]+/
};

/**
 * Componente Sugerencias optimizado
 * Muestra sugerencias predictivas con navegación por teclado eficiente.
 * 
 * @param {string} palabra - Término de búsqueda actual
 * @param {Function} setText - Actualiza el texto del input
 * @param {Function} setTextNew - Actualiza el nuevo texto
 * @param {Function} setShowSugerencias - Controla la visibilidad de sugerencias
 * @param {Object} inputRef - Referencia al input de búsqueda
 * @param {boolean} hasFocus - Indica si el input tiene foco
 * @param {Function} setQuery - Actualiza la query de búsqueda
 * @param {string} category - Categoría de filtro actual
 * @param {boolean} isMobile - Indica si es dispositivo móvil
 * @param {Function} onQueryChange - Callback al cambiar la query
 * @param {number} selectedIndex - Índice de la sugerencia seleccionada
 * @param {Function} setSelectedIndex - Función para actualizar el índice seleccionado
 */
export default function Suggestions({
  palabra,
  setText,
  setTextNew,
  setShowSugerencias,
  inputRef,
  hasFocus,
  setQuery,
  category,
  isMobile,
  onQueryChange,
  selectedIndex,
  setSelectedIndex,
}) {
  // Hooks de datos y estado
  const { listaSugerencias } = useSuggestions(palabra, category, hasFocus);

  /**
   * Limpia el texto de sugerencia removiendo palabras incompletas y caracteres especiales
   * 
   * @description Procesa el texto de sugerencias en múltiples pasos:
   * 1. Valida que sea string y no esté vacío
   * 2. Elimina espacios al inicio y final
   * 3. Remueve la última palabra (generalmente incompleta)
   * 4. Elimina caracteres especiales al inicio
   * 5. Retorna el texto limpio o el original si la limpieza falla
   * Memoizada para optimizar el procesamiento repetitivo.
   */
  const cleanSuggestionText = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    
    const trimmed = text.trim();
    if (!trimmed) return '';
    
    const withoutLastWord = trimmed.replace(TEXT_CLEANUP_REGEX.REMOVE_LAST_WORD, '');
    const cleaned = withoutLastWord.replace(TEXT_CLEANUP_REGEX.REMOVE_LEADING_SPECIAL, '');
    
    return cleaned || trimmed;
  }, []);

  /**
   * Actualiza todos los estados relacionados con la selección de una sugerencia
   * 
   * @description Sincroniza múltiples estados cuando se selecciona una sugerencia:
   * - setText: actualiza el texto local del componente
   * - setQuery: actualiza la query global de búsqueda
   * - setTextNew: actualiza el nuevo texto para navegación
   * - onQueryChange: notifica al componente padre del cambio
   * - inputRef: actualiza directamente el valor del DOM y quita el focus
   * Memoizada para evitar recreaciones innecesarias.
   */
  const updateAllStates = useCallback((nuevoValor) => {
    setText(nuevoValor);
    setQuery(nuevoValor);
    setTextNew(nuevoValor);
    onQueryChange(nuevoValor);
    
    if (inputRef.current) {
      inputRef.current.value = nuevoValor;
      inputRef.current.blur(); // Quita focus para ocultar sugerencias
    }
  }, [setText, setQuery, setTextNew, onQueryChange, inputRef]);

  /**
   * Cierra la lista de sugerencias y resetea el índice seleccionado
   * 
   * @description Oculta las sugerencias y limpia el estado de selección.
   * Incluye validación de tipo para setShowSugerencias por seguridad.
   * Se utiliza cuando el usuario cancela la selección o completa una búsqueda.
   * Memoizada con useCallback para evitar re-renders innecesarios.
   */
  const closeSuggestions = useCallback(() => {
    setSelectedIndex(-1);
    if (typeof setShowSugerencias === 'function') {
      setShowSugerencias(false);
    }
  }, [setSelectedIndex, setShowSugerencias]);

  /**
   * Maneja la selección de una sugerencia, limpia el texto y actualiza estados
   * 
   * @description Procesa la selección de una sugerencia ejecutando tres pasos:
   * 1. Limpia el texto de la sugerencia usando cleanSuggestionText
   * 2. Actualiza todos los estados relacionados (input, query, etc.)
   * 3. Cierra la lista de sugerencias
   * Memoizada para optimizar rendimiento en re-renders.
   */
  const handleSelect = useCallback((valor) => {
    const nuevoValor = cleanSuggestionText(valor);
    updateAllStates(nuevoValor);
    closeSuggestions();
  }, [cleanSuggestionText, updateAllStates, closeSuggestions]);

  /**
   * Crea un handler memoizado para la selección de una sugerencia específica
   */
  const createSelectHandler = useCallback((sugerencia) => {
    return () => handleSelect(sugerencia);
  }, [handleSelect]);

  /**
   * Configura la navegación por teclado para las sugerencias
   * 
   * @description Hook personalizado que maneja la navegación con teclas de flecha
   * (ArrowUp/ArrowDown) y selección con Enter/Escape en la lista de sugerencias.
   * Solo se activa cuando el input tiene focus y hay sugerencias disponibles.
   * 
   * Parámetros del hook:
   * - listaSugerencias: Array de sugerencias para navegar
   * - selectedIndex: Índice actual seleccionado
   * - setSelectedIndex: Función para actualizar el índice
   * - handleSelect: Callback para seleccionar una sugerencia
   * - closeSuggestions: Callback para cerrar las sugerencias
   * - isActive: Condición para activar la navegación
   */
  useKeyboardNavigation(
    listaSugerencias, // Lista original sin modificar
    selectedIndex,
    setSelectedIndex,
    handleSelect,
    closeSuggestions,
    hasFocus && listaSugerencias?.length > 0
  );

  /**
   * Mapea una sugerencia agregando el texto limpio para mostrar
   * 
   * @description Función de transformación que enriquece cada sugerencia
   * con una propiedad cleanText que contiene el texto procesado y limpio.
   * Utiliza spread operator para mantener todas las propiedades originales
   * y agrega la nueva propiedad. Memoizada para evitar recreaciones
   * innecesarias durante el procesamiento de listas.
   */
  const mapSuggestionWithCleanText = useCallback((suggestion) => ({
    ...suggestion,
    cleanText: cleanSuggestionText(suggestion.sugerencia)
  }), [cleanSuggestionText]);

  /**
   * Procesa y limita las sugerencias según el dispositivo (mobile/desktop)
   * 
   * @description Valor memoizado que prepara las sugerencias para renderizado:
   * - Desktop: limita a 6 sugerencias (DESKTOP_SUGGESTIONS_LIMIT)
   * - Mobile: limita a 3 sugerencias (MOBILE_SUGGESTIONS_LIMIT)
   * Aplica mapSuggestionWithCleanText a cada sugerencia para agregar cleanText.
   * Optimizado con useMemo para evitar reprocesamiento innecesario cuando
   * las dependencias no cambian.
   */
  const displayedSuggestions = useMemo(() => {
    if (!listaSugerencias?.length) return [];
    
    const limit = isMobile ? MOBILE_SUGGESTIONS_LIMIT : DESKTOP_SUGGESTIONS_LIMIT;
    const limitedSuggestions = listaSugerencias.slice(0, limit);
    return limitedSuggestions.map(mapSuggestionWithCleanText);
  }, [listaSugerencias, isMobile, mapSuggestionWithCleanText]);

  /**
   * Determina si se debe renderizar el componente de sugerencias
   * 
   * @description Evalúa dos condiciones necesarias para mostrar las sugerencias:
   * 1. hasFocus: el input debe tener focus actualmente
   * 2. listaSugerencias?.length > 0: debe haber al menos una sugerencia disponible
   * Si cualquiera de estas condiciones es falsa, el componente retorna null.
   */
  const shouldShowSuggestions = hasFocus && listaSugerencias?.length > 0;

  /**
   * Guard clause: evita renderizar el componente si no se cumplen las condiciones
   * 
   * @description Retorna null si shouldShowSuggestions es false, lo que significa
   * que el input no tiene focus o no hay sugerencias disponibles. Esto optimiza
   * el rendimiento evitando renderizar el DOM innecesariamente.
   */
  if (!shouldShowSuggestions) return null;

  return (
    <div className='lista-sugerencias' role="listbox" aria-label="Sugerencias de búsqueda">
      <ul>
        {displayedSuggestions.map((suggestion, index) => (
          <SuggestionItem
            key={`${suggestion.sugerencia}-${index}`}
            suggestion={suggestion}
            index={index}
            isSelected={index === selectedIndex}
            onSelect={createSelectHandler(suggestion.sugerencia)}
          />
        ))}
      </ul>
    </div>
  );
};